import React, {useContext, useEffect, useRef, useState} from 'react';
import {Badge, OverlayTrigger, Spinner, Tooltip} from 'react-bootstrap'
import cellEditFactory, {Type} from 'react-bootstrap-table2-editor';
import {DateTime} from "luxon";
import Table, {
    buildMultiSelectFilterRenderer,
    buildSelectFilter,
    eqCompMultiSelectFilter,
    likeCompMultiSelectFilter,
    tableNumberFilter,
    tableTextFilter
} from "../Table/Table";
import {ProjectColors} from '../../Enums/Core/projectColors'
import {FaSalesforce} from 'react-icons/fa';
import {RiExternalLinkLine} from 'react-icons/ri';
import {
    DATE_LOCALE,
    WINT_AI_CONTROL_URL,
    WINT_LIGHTNING_FORCE_URL,
    WINT_ORGANIZATIONS_ROUTE,
    WINT_SITES_ROUTE,
    WINT_WATER_SYSTEMS_ROUTE
} from "../../Constants/constants";
import {AiFillPhone} from "react-icons/ai";
import {MdEmail} from "react-icons/md";
import {IoMdText} from "react-icons/io";

import 'react-bootstrap-table2-filter/dist/react-bootstrap-table2-filter.min.css';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';
import 'react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css';
import './AlertsTable.css'
import {ValveErrors} from "../../Enums/MonitoringPage/valveErrors";
import {DateFormatter} from "../../General/SharedMethods";
import TextEditor from "./TextEditor";
import {store} from '../../Store/store'
import {
    SET_IS_REQUEST_IN_PROGRESS,
    TOGGLE_COMM_CHECKBOX,
    UPDATE_ALERTS_STATE_ARR
} from "../../Store/ActionTypes/General/actionTypes";
import ReasonDropdownEditor from "./ReasonDropdownEditor";
import ConfirmationDropdownEditor from "./ConfirmationDropdownEditor";
import AssigneeDropdownEditor from "./AssigneeDropdownEditor";

export default function AlertsTable(
    {
        data,
        monitoringService,
        confirmationData,
        supportUsers,
        reasons,
        confirmations,
        handleOnEdit,
        getAllData,
        isRequestInProgress,
        setSelectedSystemStates
    }) {
    const [sortState, setSortState] = useState({dataField: 'lastEventTime', order: 'desc'});
    const repeatingLeaksData = useRef([]);
    const {dispatch} = useContext(store);
    const stateObj = useContext(store);
    const NUMBER_OF_FILTERS = 4;
    let alertsStateArr;

    useEffect(() => {
        initStateData();
    }, [data])

    const initStateData = () => {
        let arr = [];
        if (data) {
            data.forEach(obj => {
                    arr.push({
                        id: obj.id,
                        comm: obj.communication,
                        remarks: obj.remarks,
                        confirmation: obj.confirmation,
                        reason: obj.reason,
                        assignee: obj.assignee
                    });
                }
            )
            updateStateArr(arr);
        }
    }

    const updateStateArr = (dataArr) => {
        dispatch({
            type: UPDATE_ALERTS_STATE_ARR, payload: dataArr
        });
    }

    const onSort = (field, order) => {
        setSortState({dataField: field, order: order});
    }

    // region Formatters

    const severityFormatter = (cell, row) => {
        let variant;
        if (cell === 'Minor') {
            variant = 'success';
        }
        if (cell === 'Moderate') {
            variant = 'warning';
        }
        if (cell === 'Major') {
            variant = 'danger';
        }
        return <Badge variant={variant}>{cell}</Badge>;
    }

    const valveStatusFormatter = (cell) => {
        if (!cell) {
            return '';
        }
        const matchingOption = valveStatusSelectOptions.find(option => option.value === cell);
        if (!matchingOption) {
            return <Badge variant={'warning'}>Unknown</Badge>;
        }
        let variant;
        switch (cell) {
            case 'OPEN':
            case 'OPEN_AND_VALVE_ERROR':
                variant = 'danger';
                break;
            case 'UNKNOWN':
                variant = 'warning';
                break;
            default:
                variant = 'success';
        }

        return <Badge variant={variant}>{matchingOption.label}</Badge>;
    }

    const leakAlertFormatter = (cell) => {
        if (cell === undefined || cell === null) {
            return;
        }
        return cell ? 'Yes' : 'No';
    }

    const dateFormatter = (cell) => DateFormatter(cell);

    const waterSystemFormatter = (cell, row) => {
        let href = `${WINT_AI_CONTROL_URL}/${WINT_SITES_ROUTE}/${row.siteId}/${WINT_WATER_SYSTEMS_ROUTE}/${row.waterSystemId}`;
        return <OverlayTrigger placement="top" overlay={<Tooltip> {cell}</Tooltip>}>
            <a className="alert-table-link-black" target="_blank" rel="noreferrer" href={href}>{row.waterSystem}</a>
        </OverlayTrigger>
    }

    const siteFormatter = (cell, row) => {
        let href = `${WINT_AI_CONTROL_URL}/${WINT_SITES_ROUTE}/${row.siteId}`;

        return <OverlayTrigger placement="top" overlay={<Tooltip> {cell}</Tooltip>}>
            <a className="alert-table-link-black" target="_blank" rel="noreferrer" href={href}>{row.site}</a>
        </OverlayTrigger>

    }

    const organizationFormatter = (cell, row) => {
        let href = `${WINT_AI_CONTROL_URL}/${WINT_ORGANIZATIONS_ROUTE}/${row.organizationId}/`;
        return <OverlayTrigger placement="top" overlay={<Tooltip> {cell}</Tooltip>}>
            <a className="alert-table-link-black" target="_blank" rel="noreferrer" href={href}>{row.account}</a>
        </OverlayTrigger>
    }

    const durationFormatter = (cell) => {
        if (!cell) return '00:00:00';

        let numOfSecondsInDay = 60 * 60 * 24;
        let days = Math.floor(cell / numOfSecondsInDay);
        let time = new Date(cell * 1000).toISOString().substr(11, 8);

        return (days > 0) ? `${days} days, ${time}` : `${time}`;
    }

    const othersFormatter = (cell) => {
        if (!cell) {
            return '';
        }
        const matchingOption = othersFilterOptions.find(option => option.value === cell);
        if (!matchingOption) {
            return '';
        }
        let variant;
        if (cell === ValveErrors.MANUAL_OVERRIDE || cell === ValveErrors.AUTO_SHUTOFF_DISABLED || ValveErrors.AUTO_SHUTOFF_DISABLED_AND_MANUAL_OVERRIDE) {
            variant = 'warning';
        }

        return (variant) ? <Badge variant={variant}>{matchingOption.label}</Badge> : matchingOption.label;
    }

    const commFormatter = (cell, row) => {
        return <CommEditor row={row} setIsRequestInProgress={setIsRequestInProgress}
                           monitoringService={monitoringService} getConfirmationEnum={getConfirmationEnum}
                           getReasonEnum={getReasonEnum} updateCommCheckBoxArr={updateStateArr}
                           getAlertsStateForRow={getAlertsStateForRow}/>;
    }

    const assigneeFormatter = (cell) => {
        return assigneeEditor.options.filter(user => {
            return user.value === Number(cell)
        })[0]?.label;
    }

    const salesforceHeaderFormatter = () => {
        return <FaSalesforce style={{color: ProjectColors.Aqua}}/>
    }

    const salesforceLinkFormatter = (cell, row) => {
        if (!cell) return '';
        let href = cell;
        return <RiExternalLinkLine className="alerts-salesforce-button" onClick={() => window.open(href)}/>
    }

    const csvSalesforceIdFormatter = (cell, row) => {
        if (!cell) return '';

        return `${WINT_LIGHTNING_FORCE_URL}/lightning/r/Event_Log__c/${row.salesforceId}/view`
    }
    const leakConsumptionFormatter = (cell) => {
        return Number(cell).toLocaleString();
    }

    const remarksFormatter = (cell) => {
        if (!cell) return '';

        if (cell.startsWith('http')) {
            let className = 'alert-table-link-black';
            return <a className={className} target="_blank" rel="noreferrer" href={cell}>{cell}</a>
        }

        return cell;
    }

    let confirmationFilterOptions = confirmationData.filter((v, i, a) => a.findIndex(t => (t.value === v.value)) === i);

    let reasonsOptions = confirmationData.map((reason) => {
        return ({...reason, label: reason.text})
    });

    const confirmationFormatter = (cell) => {
        return confirmationEditor.options.filter(conf => {
            return conf.value === Number(cell)
        })[0]?.label;
    }

    const reasonsFormatter = (cell, row) => {
        const reasonValue = reasonEditor.options.filter(reason => {
            return reason.id === row.reason
        });
        return reasonValue[0]?.label || "";
    }

    const remarksCsvFormatter = (cell) => {
        if (!cell) return '';

        return cell;
    }

    // endregion

    // region Filters

    const filtersRefs = useRef([]);
    const severityFilterRef = useRef();
    const assigneeFilterRef = useRef();
    const othersFilterRef = useRef();
    const valveStatusFilterRef = useRef();
    // Hidden column refs (kept for column definitions but not used in resetFilters)
    const tierFilterRef = useRef();
    const confirmationFilterRef = useRef();
    const reasonFilterRef = useRef();

    const othersFilterOptions = [
        {"value": "AUTO_SHUTOFF_DISABLED", "label": "Auto-Shutoff Disabled"},
        {"value": "MANUAL_OVERRIDE", "label": "Manual Override"},
        {"value": "AUTO_SHUTOFF_DISABLED_AND_MANUAL_OVERRIDE", "label": "Auto-Shutoff Disabled, Manual Override "}
    ];


    const valveStatusSelectOptions = [
        {"value": "OPEN", "label": "Open"},
        {"value": "CLOSED", "label": "Closed"},
        {"value": "UNKNOWN", "label": "Unknown"},
        {"value": "NO_VALVE", "label": "No Valve"},
        {"value": "OPEN_AND_VALVE_ERROR", "label": "Open, Valve Error"},
        {"value": "CLOSED_AND_VALVE_ERROR", "label": "Closed, Valve Error"}
    ];

    const leakAlertsSelectOptions = {
        true: 'Yes', false: 'No'
    };


    const defaultValveStatusOptionsForFilter = valveStatusSelectOptions.filter(option => option.value !== "CLOSED");

    const tierFilterOptions = [
        {value: 1, label: 'Tier 1'},
        {value: 2, label: 'Tier 2'},
        {value: 3, label: 'Tier 3'},
        {value: 4, label: 'Tier 4'}
    ];

    const defaultTierOptionsForFilter = tierFilterOptions.filter(option => option.value !== 4);

    const severityFilterOptions = [
        {value: 'Major', label: 'Major'},
        {value: 'Moderate', label: 'Moderate'},
        {value: 'Minor', label: 'Minor'}
    ];

    const defaultSeverityOptionsForFilter = severityFilterOptions.filter(option => option.value !== "Minor");

    const assigneeFilterOptions = supportUsers.map(user => {
        return {value: user.id, label: user.name}
    });

    const reasonsFilterOptions = reasons.map((reason) => {
        return ({value: reason.id, label: reason.text});
    });

    // endregion

    // region Table Editors

    const assigneeEditor = {
        type: Type.SELECT, options: [{
            value: 0, label: 'Unassigned'
        }].concat(assigneeFilterOptions)
    }

    const confirmationEditor = {
        type: Type.SELECT, options: confirmationFilterOptions
    }

    const reasonEditor = {
        type: Type.SELECT, options: reasonsOptions
    }

    // endregion

    // region Styles

    const colStyle = {fontSize: 12, padding: '1px', verticalAlign: 'center'};
    const headerStyle = {fontSize: 12};
    const textColStyle = {maxWidth: 100, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis'};

    const commColStyle = {fontSize: 14, padding: '1px', verticalAlign: 'center', color: 'black'};

    const valveErrorColStyle = (cell) => {
        let styleObj = {fontSize: 12, padding: '1px', verticalAlign: 'center'};

        if (cell === 1) {
            styleObj.fontWeight = 700;
        }

        return styleObj;
    }

    // endregion

    const setIsRequestInProgress = (state, rowId) => {
        dispatch({
            type: SET_IS_REQUEST_IN_PROGRESS, payload: {
                isRequestInProgress: {state: state, rowId: rowId}
            }
        });
    }
    const toggleCommCheckBox = (state, rowId) => {
        dispatch({
            type: TOGGLE_COMM_CHECKBOX, payload: {
                isCheckboxDisabled: {state: state, rowId: rowId}
            }
        });
    }

    // region Table Properties

    const rowStyle = (row) => {
        switch (row.leakHighlight) {
            case 'MAJOR_LEAK':
                row.bgColor = 'rgba(255,69,49,0.8)';
                return {backgroundColor: 'rgba(255,69,49,0.8)'};
            case 'MAJOR_LEAK_ATTENTION':
                row.bgColor = 'rgba(165,17,1,0.8)';
                return {backgroundColor: 'rgba(165,17,1,0.8)', color: "white"};
            case 'MODERATE_LEAK':
                row.bgColor = 'rgba(242,219,39,0.8)';
                return {backgroundColor: 'rgba(242,219,39,0.8)'};
            case 'MODERATE_LEAK_ATTENTION':
                row.bgColor = 'rgba(230,134,18,0.8)';
                return {backgroundColor: 'rgba(230,134,18,0.8)'};
            case  'MINOR_LEAK_ATTENTION':
                row.bgColor = 'rgba(238,66,169,0.8)';
                return {backgroundColor: 'rgba(238,66,169,0.8)', color: "white"};
            case 'REPEATING_LEAK':
                row.bgColor = 'rgba(132,3,252,0.8)';
                return {backgroundColor: 'rgba(132,3,252,0.8)', color: "white"};
            default:
                return;
        }
    }

    const cellEdit = cellEditFactory({
        mode: 'click',
        blurToSave: true,
        autoSelectText: true,
        beforeSaveCell: async (oldValue, newValue, row, column) => {
            alertsStateArr = stateObj.state.alertsStateArr;
            let newArr;
            if (newValue === 'reasonChanged') {
                return;
            }
            if (column.dataField !== "remarks") newValue = Number(newValue);
            if (oldValue === newValue) return;
            if (column.dataField === "confirmation" && newValue !== 0) return;
            if (column.dataField === "remarks") {
                newArr = alertsStateArr.map(object => {
                    if (object.id === row.id) {
                        return {...object, remarks: newValue}
                    }
                    return object;
                });
                updateStateArr(newArr);
            }
            if (column.dataField === "assignee") {
                newArr = alertsStateArr.map(object => {
                    if (object.id === row.id) {
                        return {...object, assignee: newValue};
                    }
                    return object;
                });
                updateStateArr(newArr);
            }
            row[column.dataField] = newValue;
            if (column.dataField === "confirmation") {
                row['reason'] = reasonsOptions.filter((option) => option.value === newValue)[0].id;
            }
            setIsRequestInProgress(true, row.id);
            monitoringService.updateEditableFields(row.id, row.assignee, row.remarks, getConfirmationEnum(row.confirmation), getReasonEnum(row.reason), row.exclude, getAlertsStateForRow(alertsStateArr, row).comm).then(() => {
                setIsRequestInProgress(false, row.id);
            }).catch(err => {
                setIsRequestInProgress(false, row.id);
                console.log("err === ", err);
            })
        }
    });

    const getConfirmationEnum = (confirmationId) => {
        const result = confirmations.filter((obj) => obj.id === confirmationId);
        return result[0] ? result[0].value : 'UNKNOWN';

    }

    const getReasonEnum = (reasonId) => {
        const result = reasons.filter((obj) => obj.id === reasonId)
        const resultValue = result[0] ? result[0].text : '';
        let enumText = resultValue === '' ? 'UNKNOWN' : resultValue.replaceAll(' ', '_').toUpperCase();
        return enumText;
    }

    const getAlertsStateForRow = (alertsStateArr, row) => {
        const filterResult = alertsStateArr.filter(obj => obj.id === row.id);
        const alertsStateObj = filterResult[0];
        return alertsStateObj;
    }

    const numberSortFunc = (a, b, order) => {
        if (order === 'asc') {
            return b - a;
        }
        return a - b; // desc
    }

    const dateSorter = (a, b, order) => {
        return order === 'asc' ? Date.parse(a) - Date.parse(b) : Date.parse(b) - Date.parse(a);
    }

    const resetFilters = () => {
        if (filtersRefs.current.length > NUMBER_OF_FILTERS) {
            filtersRefs.current.splice(0, filtersRefs.current.length - NUMBER_OF_FILTERS);
        }
        filtersRefs.current.forEach(currFilter => {
            if (currFilter) currFilter('');
        })

        severityFilterRef.current.resetFilter();
        tierFilterRef.current.resetFilter();
        assigneeFilterRef.current.resetFilter();
        valveStatusFilterRef.current.resetFilter();
        othersFilterRef.current.resetFilter();
    }

    const columns = [
        {
            dataField: 'lastEventTime',
            isDefault: true,
            text: 'Date',
            headerStyle: headerStyle,
            style: colStyle,
            filter: tableTextFilter(filtersRefs),
            sort: true,
            sortFunc: dateSorter,
            editable: false,
            csvFormatter: dateFormatter,
            onSort: onSort
        }, {
            dataField: 'linkToSalesForce',
            adminColumn: true,
            text: 'SalesForce',
            headerStyle: {fontSize: 40, width: '3%'},
            headerFormatter: salesforceHeaderFormatter,
            style: {fontSize: 12, padding: '1px', verticalAlign: 'center'},
            sort: false,
            editable: false,
            formatter: salesforceLinkFormatter,
            csvFormatter: csvSalesforceIdFormatter,
            onSort: onSort
        },
        {
            dataField: 'waterSystem',
            headerStyle: headerStyle,
            text: 'Water System',
            style: textColStyle,
            sort: true,
            filter: tableTextFilter(filtersRefs),
            editable: false,
            formatter: waterSystemFormatter,
            onSort: onSort
        },
        {
            dataField: 'account',
            text: 'Account',
            headerStyle: headerStyle,
            style: textColStyle, sort: true,
            filter: tableTextFilter(filtersRefs),
            editable: false,
            formatter: organizationFormatter,
            onSort: onSort
        },
        {
            dataField: 'site',
            text: 'Site',
            headerStyle: headerStyle,
            style: textColStyle,
            sort: true,
            filter: tableTextFilter(filtersRefs),
            editable: false,
            formatter: siteFormatter,
            onSort: onSort
        },
        {
            dataField: 'duration',
            text: 'Duration',
            headerStyle: headerStyle,
            style: colStyle,
            sort: true,
            sortFunc: numberSortFunc,
            editable: false,
            formatter: durationFormatter,
            onSort: onSort
        },
        {
            dataField: 'leakConsumption',
            text: 'Leak Consumption',
            headerStyle: headerStyle,
            style: colStyle,
            sort: true,
            sortFunc: numberSortFunc,
            editable: false,
            formatter: leakConsumptionFormatter,
            onSort: onSort
        },
        {
            dataField: "initialFlowRate",
            text: 'Flow Rate (Alert)',
            headerStyle: headerStyle,
            style: colStyle,
            filter: tableNumberFilter(filtersRefs),
            sort: true,
            sortFunc: numberSortFunc,
            editable: false,
            formatter: leakConsumptionFormatter,
            onSort: onSort
        },
        {
            dataField: 'flowRatePerHour',
            text: 'Flow Rate – Accumulate',
            headerStyle: headerStyle,
            style: colStyle,
            filter: tableNumberFilter(filtersRefs),
            sort: true,
            sortFunc: numberSortFunc,
            editable: false,
            formatter: leakConsumptionFormatter,
            onSort: onSort
        },
        {
            dataField: 'tier',
            text: 'Tier',
            headerStyle: headerStyle,
            style: colStyle,
            sort: true,
            filter: likeCompMultiSelectFilter,
            filterRenderer: buildMultiSelectFilterRenderer(tierFilterRef, tierFilterOptions),
            editable: false,
            onSort: onSort
        },
        {
            dataField: 'severity',
            text: 'Severity',
            headerStyle: headerStyle,
            formatter: severityFormatter,
            style: colStyle,
            sort: true,
            filter: likeCompMultiSelectFilter,
            filterRenderer: buildMultiSelectFilterRenderer(severityFilterRef, severityFilterOptions, defaultSeverityOptionsForFilter),
            editable: false,
            onSort: onSort
        },
        {
            dataField: 'valveStatus',
            text: 'Valve Status',
            headerStyle: headerStyle,
            style: colStyle,
            sort: true,
            editable: false,
            filter: eqCompMultiSelectFilter,
            filterRenderer: buildMultiSelectFilterRenderer(valveStatusFilterRef, valveStatusSelectOptions, defaultValveStatusOptionsForFilter),
            formatter: valveStatusFormatter,
            onSort: onSort
        },
        {
            dataField: 'leakAlerts',
            text: 'Leak Alerts',
            headerStyle: headerStyle,
            style: colStyle,
            sort: true,
            editable: false,
            filter: buildSelectFilter(filtersRefs, leakAlertsSelectOptions, true),
            formatter: leakAlertFormatter,
            onSort: onSort
        },

        {
            dataField: 'other',
            text: 'Others',
            headerStyle: headerStyle,
            style: colStyle,
            sort: true,
            filter: likeCompMultiSelectFilter,
            filterRenderer: buildMultiSelectFilterRenderer(othersFilterRef, othersFilterOptions),
            editable: false,
            formatter: othersFormatter,
            onSort: onSort
        },
        {
            dataField: 'assignee',
            adminColumn: true,
            text: 'Assignee',
            headerStyle: headerStyle,
            style: colStyle,
            sort: true,
            filter: likeCompMultiSelectFilter,
            filterRenderer: buildMultiSelectFilterRenderer(assigneeFilterRef, assigneeFilterOptions),
            editorRenderer: (editorProps, value, row, column) => {
                let options = [{
                    value: 0, label: 'Unassigned'
                }].concat(assigneeFilterOptions);
                return <AssigneeDropdownEditor {...editorProps} options={options} handleOnEdit={handleOnEdit}
                                               value={value} row={row} toggleCommCheckBox={toggleCommCheckBox}/>;
            },
            formatter: assigneeFormatter,
            csvFormatter: assigneeFormatter,
            onSort: onSort
        },
        {
            dataField: 'remarks',
            adminColumn: true,
            text: 'Remarks',
            headerStyle: headerStyle,
            style: colStyle,
            sort: true,
            filter: tableTextFilter(filtersRefs),
            editable: true,
            editorRenderer: (editorProps, value, row, column) => {
                return <TextEditor {...editorProps} handleOnEdit={handleOnEdit} value={value}
                                   row={row} toggleCommCheckBox={toggleCommCheckBox}/>;
            }, formatter: remarksFormatter,
            csvFormatter: remarksCsvFormatter,
            onSort: onSort
        },
        {
            dataField: 'communication',
            adminColumn: true,
            text: 'Comm',
            headerStyle: headerStyle,
            style: commColStyle,
            editable: false,
            formatter: commFormatter,
            onSort: onSort
        },
        {
            dataField: 'confirmation',
            adminColumn: true,
            text: 'Confirmation',
            headerStyle: headerStyle,
            style: colStyle,
            sort: true,
            filter: likeCompMultiSelectFilter,
            filterRenderer: buildMultiSelectFilterRenderer(confirmationFilterRef, confirmationFilterOptions),
            editorRenderer: (editorProps, value, row, column) => {
                return <ConfirmationDropdownEditor handleOnEdit={handleOnEdit} value={value}
                                                   toggleCommCheckBox={toggleCommCheckBox}
                                                   updateStateArr={updateStateArr}
                                                   row={row}
                                                   options={confirmationFilterOptions} {...editorProps} />;
            },
            formatter: confirmationFormatter,
            csvFormatter: confirmationFormatter,
            onSort: onSort,
            hidden: true
        }, // When saving the reason to the DB need to save the reason id and not text
        {
            dataField: 'reason',
            adminColumn: true,
            text: 'Reason',
            headerStyle: headerStyle,
            style: colStyle,
            sort: true,
            filter: likeCompMultiSelectFilter,
            filterRenderer: buildMultiSelectFilterRenderer(reasonFilterRef, reasonsFilterOptions),
            editorRenderer: (editorProps, value, row, column) => {
                return <ReasonDropdownEditor row={row} column={column} handleOnEdit={handleOnEdit}
                                             value={value} toggleCommCheckBox={toggleCommCheckBox}
                                             reasonsOptions={reasonsOptions} {...editorProps}
                                             setIsRequestInProgress={setIsRequestInProgress}
                                             monitoringService={monitoringService}
                                             getConfirmationEnum={getConfirmationEnum}
                                             getReasonEnum={getReasonEnum}
                                             getAlertsStateForRow={getAlertsStateForRow}
                                             updateStateArr={updateStateArr}
                />;
            },
            formatter: reasonsFormatter,
            csvFormatter: reasonsFormatter,
            onSort: onSort,
            hidden: true
        },]

    // endregion

    useEffect(async () => {
        setIsRequestInProgress(false);
        toggleCommCheckBox(false);
    }, [])

    return (<>
        {!data && <Spinner animation="border" variant="primary"/>}
        {data && <Table
            data={data}
            columns={columns}
            systemCycleCallBack={getAllData}
            isRequestInProgress={isRequestInProgress}
            setSelectedSystemStates={setSelectedSystemStates}
            exportCsv
            csvFileName={`Alerts_Report_${DateTime.local().toLocaleString({locale: DATE_LOCALE})}.csv`}
            resetFilters={resetFilters}
            rowStyle={rowStyle}
            defaultSort={sortState}
            cellEdit={cellEdit}/>}
    </>);
}

function useForceUpdate() {
    const [value, setValue] = useState(0); // integer state
    return () => setValue(value => value + 1); // update the state to force render
}

function CommEditor({
                        row,
                        monitoringService,
                        getConfirmationEnum,
                        getReasonEnum,
                        setIsRequestInProgress,
                        updateCommCheckBoxArr,
                        getAlertsStateForRow
                    }) {
    const stateObj = useContext(store);
    const isRequestInProgress = stateObj.state.isRequestInProgress;
    const isCheckboxDisabled = stateObj.state.isCheckboxDisabled;
    const alertsStateArr = stateObj.state.alertsStateArr;
    const checkboxDisabled = isRequestInProgress.state && isRequestInProgress.rowId === row.id || isCheckboxDisabled.state && isCheckboxDisabled.rowId === row.id;
    const forceUpdate = useForceUpdate();
    const phoneCheckBoxRef = useRef(null);
    const smsCheckBoxRef = useRef(null);
    const emailCheckBoxRef = useRef(null);
    const excludedField = row.excluded
    const [isPhoneChecked, setPhoneIsChecked] = useState(row.communication.call);
    const [isSmsChecked, setSmsIsChecked] = useState(row.communication.message);
    const [isEmailChecked, setEmailIsChecked] = useState(row.communication.email);

    const saveOnClick = async () => {
        const isPhoneCheckboxChecked = phoneCheckBoxRef.current.checked;
        const isSmsCheckboxChecked = smsCheckBoxRef.current.checked;
        const isEmailCheckboxChecked = emailCheckBoxRef.current.checked;
        setPhoneIsChecked(isPhoneCheckboxChecked);
        setSmsIsChecked(isSmsCheckboxChecked);
        setEmailIsChecked(isEmailCheckboxChecked);
        const newArr = alertsStateArr.map(object => {
            if (object.id === row.id) {
                return {
                    ...object, comm: {
                        call: isPhoneCheckboxChecked,
                        message: isSmsCheckboxChecked,
                        email: isEmailCheckboxChecked
                    }
                };
            }
            return object;
        });
        updateCommCheckBoxArr(newArr);
        const leakId = row.id;
        const confirmationField = getAlertsStateForRow(alertsStateArr, row).confirmation;
        const reasonField = getAlertsStateForRow(alertsStateArr, row).reason;
        const communicationObj = {
            call: isPhoneCheckboxChecked,
            message: isSmsCheckboxChecked,
            email: isEmailCheckboxChecked
        }
        monitoringService.updateEditableFields(leakId, getAlertsStateForRow(alertsStateArr, row).assignee, getAlertsStateForRow(alertsStateArr, row).remarks, getConfirmationEnum(confirmationField), getReasonEnum(reasonField), excludedField, communicationObj)
            .then(() => {
                setIsRequestInProgress(false, row.id);
            }).catch(error => {
            setIsRequestInProgress(false, row.id);
            console.log(error);
        })
    }

    return (<>
        <input type="checkbox" onClick={forceUpdate}
               disabled={checkboxDisabled}
               checked={isPhoneChecked}
               ref={phoneCheckBoxRef}
               onChange={() => saveOnClick()}/>
        <AiFillPhone/>
        <input type="checkbox" onClick={forceUpdate}
               disabled={checkboxDisabled}
               checked={isSmsChecked}
               ref={smsCheckBoxRef}
               onChange={() => saveOnClick()}/>
        <IoMdText/>
        <input type="checkbox" onClick={forceUpdate}
               disabled={checkboxDisabled}
               checked={isEmailChecked}
               ref={emailCheckBoxRef}
               onChange={() => saveOnClick()}/>
        <MdEmail/>
    </>);
}
